import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { apiErrorText } from 'Utils/errorText';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import {
  updateDataGraph, rowFilterFor, withRowFilter, graphUpdateBody, pickableRecordTypes, recordTypeFilter, recordTypesInFilter,
} from './dataGraphApi';

const LANGUAGE = {
  salesforce: { noun: 'object', placeholder: "RecordType.DeveloperName = 'HH_Account'", hint: 'SOQL WHERE expression' },
  mongodb: { noun: 'collection', placeholder: '{"status": "ACTIVE"}', hint: 'JSON filter document' },
  sql: { noun: 'table', placeholder: "status = 'ACTIVE'", hint: 'SQL condition' },
};
export const filterLanguage = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return LANGUAGE.salesforce;
  if (e === 'mongodb') return LANGUAGE.mongodb;
  return LANGUAGE.sql;
};

// One table's row filter in a data graph: the expression the TDM worker ANDs into every query of
// the table — as the start table, a child, a parent pulled for closure, a follower's key table.
// A Salesforce object with record types offers them as checkboxes that write the SOQL (by
// DeveloperName: RecordType itself is a setup object and is not in the object list).
const RowFilterEditor = ({ graph, table, engine, recordTypes, canEdit, onSaved }) => {
  const current = rowFilterFor(graph, table) || '';
  const [text, setText] = useState(current);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => { setText(current); setNotice(null); }, [current, table]);

  const lang = filterLanguage(engine);
  const types = String(engine || '').toLowerCase() === 'salesforce' ? pickableRecordTypes(recordTypes) : [];
  const picked = recordTypesInFilter(text);
  const toggleType = (developerName) => {
    const now = picked || [];
    setText(recordTypeFilter(now.includes(developerName) ? now.filter((d) => d !== developerName) : [...now, developerName]));
    setNotice(null);
  };

  const save = async (expression) => {
    setBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, graphUpdateBody(graph, withRowFilter(graph, table, expression)));
      setNotice({ tone: 'ok', text: expression.trim() ? 'Saved.' : 'Removed.' });
      if (onSaved) onSaved(saved);
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not save the row filter') });
    } finally {
      setBusy(false);
    }
  };

  if (!graph || !table) return null;
  const disabled = !canEdit || busy;
  return (
    <div className='dg-filter' data-testid='dg-row-filter'>
      <div className='dg-section__title'>Row filter</div>
      {types.length > 0 && (
        <div className='dg-filter__types' data-testid='dg-row-filter-types'>
          {types.map((rt) => (
            <label key={rt.developerName} className='dg-filter__type' title={rt.developerName}>
              <input type='checkbox' disabled={disabled || picked === null} checked={Boolean(picked && picked.includes(rt.developerName))}
                onChange={() => toggleType(rt.developerName)} data-testid={`dg-row-filter-type-${rt.developerName}`} />
              {' '}{rt.name}{rt.active === false ? ' (inactive)' : ''}
            </label>
          ))}
        </div>
      )}
      <textarea className='dg-input dg-filter__text' rows={2} value={text} disabled={disabled} placeholder={lang.placeholder}
        aria-label={`Row filter on ${table} (${lang.hint})`} onChange={(e) => { setText(e.target.value); setNotice(null); }}
        data-testid='dg-row-filter-text' />
      <div className='dg-config__actions'>
        <Button size='sm' onClick={() => save(text)} disabled={disabled || text.trim() === current.trim()}
          title={!canEdit ? READ_ONLY_TITLE : undefined} data-testid='dg-row-filter-save'>
          {busy ? 'Saving…' : 'Save filter'}
        </Button>
        {current && (
          <Button size='sm' variant='link' onClick={() => save('')} disabled={disabled}
            title={!canEdit ? READ_ONLY_TITLE : undefined} data-testid='dg-row-filter-remove'>Remove</Button>
        )}
        <span className={notice && notice.tone === 'error' ? 'dg-error mt-0' : 'dg-muted'} data-testid='dg-row-filter-note'>
          {notice ? notice.text : `${lang.hint}. Only matching rows are pulled, wherever this ${lang.noun} is reached.`}
        </span>
      </div>
    </div>
  );
};

export default RowFilterEditor;
